import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import type { User, Session } from '@supabase/supabase-js'

interface Profile {
  id: string
  email: string
  role: 'admin' | 'trainer'
  status: 'pending' | 'approved' | 'rejected'
  name: string | null
  team: string | null
  approved_by?: string | null
  approved_at?: string | null
}

interface AuthStore {
  user: User | null
  session: Session | null
  profile: Profile | null
  loading: boolean
  
  // Actions
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signUp: (email: string, password: string, userData: { name: string, team?: string, role?: 'admin' | 'trainer' }) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<{ error?: string }>
  
  // Internal
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
  setProfile: (profile: Profile | null) => void
  setLoading: (loading: boolean) => void
  
  // Helpers
  isAdmin: () => boolean
  isTrainer: () => boolean
  isAuthenticated: () => boolean
  isApproved: () => boolean
  isPending: () => boolean
  
  // Debug helpers
  clearAuthState: () => void
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  session: null,
  profile: null,
  loading: true,

  signIn: async (email: string, password: string) => {
    console.log('🔐 AuthStore signIn started:', { email, timestamp: new Date().toISOString() })
    set({ loading: true })
    
    try {
      console.log('📞 Calling supabase.auth.signInWithPassword...')
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        console.error('❌ Supabase signIn error:', {
          message: error.message,
          status: error.status,
          timestamp: new Date().toISOString()
        })
        set({ loading: false })
        return { error: error.message }
      }

      console.log('✅ Supabase signIn successful - auth listener will handle state updates')
      // Auth listener will handle setting user, session, and profile
      // Don't set loading: false here, let the listener handle it
      return {}
    } catch (error: any) {
      console.error('💥 Unexpected error in signIn:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      })
      set({ loading: false })
      return { error: error.message }
    }
  },

  signUp: async (email: string, password: string, userData: { name: string, team?: string, role?: 'admin' | 'trainer' }) => {
    console.log('🔐 AuthStore signUp started:', { 
      email, 
      userData: { name: userData.name, team: userData.team, role: userData.role },
      timestamp: new Date().toISOString() 
    })
    set({ loading: true })
    
    try {
      console.log('📞 Calling supabase.auth.signUp...')
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            team: userData.team || '1. Mannschaft',
            role: userData.role || 'trainer'
          }
        }
      })

      if (error) {
        console.error('❌ Supabase signUp error:', {
          message: error.message,
          status: error.status,
          timestamp: new Date().toISOString()
        })
        set({ loading: false })
        return { error: error.message }
      }

      if (data.user) {
        console.log('✅ User created in auth.users:', {
          userId: data.user.id,
          email: data.user.email,
          emailConfirmedAt: data.user.email_confirmed_at,
          createdAt: data.user.created_at,
          timestamp: new Date().toISOString()
        })
        
        // Check if email confirmation is required
        if (!data.user.email_confirmed_at) {
          console.log('📧 Email confirmation required for user - signup flow will show confirmation message')
          set({ loading: false })
          return { error: 'Registrierung erfolgreich! Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link, den wir Ihnen gesendet haben. Überprüfen Sie auch Ihren Spam-Ordner.' }
        } else {
          console.log('✅ User email already confirmed at signup (unusual but possible)')
        }
        
        console.log('📝 Profile will be created automatically through database trigger (if configured)')
      } else {
        console.warn('⚠️ No user data returned from signUp (unusual)')
      }

      console.log('✅ SignUp completed successfully, setting loading to false')
      set({ loading: false })
      return {}
      
    } catch (error: any) {
      console.error('💥 Unexpected error in signUp:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      })
      set({ loading: false })
      return { error: error.message || 'Ein unerwarteter Fehler ist aufgetreten' }
    }
  },

  signOut: async () => {
    // Der lokale Zustand MUSS geleert werden, egal wie der Server reagiert.
    // Vorher stand hier ein blankes `await supabase.auth.signOut()` ohne
    // try/catch: hing oder scheiterte der Aufruf, lief das set() nie und der
    // Nutzer blieb eingeloggt — der Abmelden-Knopf wirkte schlicht kaputt.
    //
    // Genau das passiert nach einem Passwortwechsel: der entwertet die
    // Refresh-Tokens, und der anschliessende Logout laeuft gegen einen Token,
    // den der Server bereits verworfen hat.
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Zeitüberschreitung')), 3000)),
      ])
    } catch (error) {
      console.warn('⚠️ Server-Logout fehlgeschlagen oder zu langsam — melde lokal ab:', error)
      // scope 'local' raeumt die gespeicherte Session ohne Netzwerkaufruf weg.
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
    }

    set({
      user: null,
      session: null,
      profile: null,
      loading: false
    })
  },      updateProfile: async (updates: Partial<Profile>) => {
        const { profile } = get()
        if (!profile) return { error: 'Nicht angemeldet' }

        try {
          const { data, error } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', profile.id)
            .select()
            .single()

          if (error) {
            return { error: error.message }
          }

          set({ profile: data })
          return {}
        } catch (error: any) {
          return { error: error.message }
        }
      },

      setUser: (user) => set({ user }),
      setSession: (session) => set({ session }),
      setProfile: (profile) => set({ profile }),
      setLoading: (loading) => set({ loading }),

      isAdmin: () => get().profile?.role === 'admin',
      isTrainer: () => get().profile?.role === 'trainer',
      isAuthenticated: () => !!get().user,
      isApproved: () => get().profile?.status === 'approved',
      isPending: () => get().profile?.status === 'pending',
      
      // Debug helper to clear stuck auth state
      clearAuthState: () => {
        console.log('Clearing auth state manually')
        set({ 
          user: null, 
          session: null, 
          profile: null, 
          loading: false
        })
      },
    }))

// Auth State Listener Setup
export const initAuthListener = () => {
  const { setUser, setSession, setProfile, setLoading } = useAuthStore.getState()
  let mounted = true
  let initializationComplete = false
  
  // Initialize auth state with proper timing
  const initializeAuth = async () => {
    try {
      console.log('🔐 Initializing auth with storage validation...')
      setLoading(true)
      
      // CRITICAL: Get initial session first before setting up listeners
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (!mounted) return // Component unmounted during async operation
      
      if (error) {
        console.error('❌ Session error:', error)
        // Clear potentially corrupted storage on error
        console.log('🧹 Clearing storage due to session error')
        localStorage.clear()
        setUser(null)
        setSession(null)
        setProfile(null)
        setLoading(false)
        initializationComplete = true
        return
      }

      if (session?.user) {
        console.log('✅ Found existing session for:', session.user.email)
        setUser(session.user)
        setSession(session)
        
        try {
          // Load profile with timeout protection
          const profilePromise = supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single()
          
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Profile load timeout')), 5000)
          )
          
          const { data: profile, error: profileError } = await Promise.race([
            profilePromise,
            timeoutPromise
          ]) as any
          
          if (!mounted) return
          
          if (profileError) {
            console.error('❌ Profile loading error:', profileError)
            setProfile(null)
          } else {
            console.log('✅ Profile loaded:', profile)
            setProfile(profile || null)
          }
        } catch (error) {
          console.error('❌ Profile loading failed:', error)
          setProfile(null)
        }
      } else {
        console.log('🔓 No existing session')
        setUser(null)
        setSession(null)
        setProfile(null)
      }
      
      console.log('🏁 Auth initialization complete')
      if (mounted) {
        setLoading(false)
        initializationComplete = true
      }
    } catch (error) {
      console.error('💥 Auth initialization error:', error)
      if (mounted) {
        setUser(null)
        setSession(null)
        setProfile(null)
        setLoading(false)
        initializationComplete = true
      }
    }
  }

  // Initialize immediately
  initializeAuth()

  // Listen for auth changes with proper cleanup
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
    console.log('🔄 Auth state change:', event, session?.user?.email)
    
    if (!mounted) return // Prevent updates after cleanup
    
    // Only handle state changes after initialization is complete
    if (!initializationComplete) {
      console.log('⏭️ Skipping state change during initialization')
      return
    }
    
    if (event === 'SIGNED_OUT') {
      // Clear storage on sign out to prevent conflicts
      console.log('🧹 Clearing storage on sign out')
      localStorage.clear()
    }
    
    if (session?.user) {
      setUser(session.user)
      setSession(session)
      
      try {
        // Load profile with error handling
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()
        
        if (!mounted) return
        
        if (profileError) {
          console.error('❌ Profile loading error in state change:', profileError)
          setProfile(null)
        } else {
          setProfile(profile || null)
        }
      } catch (error) {
        console.error('❌ Profile loading failed in state change:', error)
        if (mounted) setProfile(null)
      }
    } else {
      setUser(null)
      setSession(null)
      setProfile(null)
    }
    
    if (mounted) setLoading(false)
  })

  return () => {
    mounted = false
    subscription.unsubscribe()
  }
}
